/**
 * Visor de Genio Documental en VENTANA adicional (no pestaña), para poder
 * ver los documentos mientras se trabaja en CIMA. La ventana es nombrada:
 * cada apertura reutiliza la misma ventana lateral en vez de acumular
 * pestañas. Todas las pantallas deben abrir el visor con este helper.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-07-30
 */

import { shootToast } from "codekit";

const GENIO_WINDOW_NAME = "genioVisor";

/**
 * Abre el visor de Genio en la ventana lateral, reutilizándola si existe.
 * Con windowName distinto se abren ventanas independientes (ej. comparar
 * los dos documentos de una duda de match lado a lado).
 * @param {string} path       Ruta/filtro del visor (ej. "JSONMETX->DOCNROXX=2667830")
 * @param {string} windowName Nombre de la ventana (default: la lateral única)
 */
export const openGenioVisor = (path, windowName = GENIO_WINDOW_NAME) => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!baseUrl) {
    return;
  }
  const width = Math.min(1100, Math.floor(window.screen.availWidth * 0.55));
  const height = window.screen.availHeight - 80;
  const left = window.screen.availWidth - width - 8;
  const features = `popup=yes,width=${width},height=${height},left=${left},top=40,resizable=yes,scrollbars=yes`;
  const genioWindow = window.open(`${baseUrl}/${path}`, windowName, features);
  if (genioWindow) {
    genioWindow.focus();
  }
};

/**
 * Abre el visor filtrado por los documentos de un DO
 * @param {string|number} docnroxx Número del DO
 */
export const openGenioVisorByDo = (docnroxx) => openGenioVisor(`JSONMETX->DOCNROXX=${docnroxx}`);

/**
 * Sanitiza un número de documento para el filtro del visor (los "/" no
 * viajan en la URL del visor: se reemplazan por "---")
 * @param {string} docNumber Número del documento
 * @returns {string} Número sanitizado y URL-encoded
 */
const sanitizeDocNumber = (docNumber) => encodeURIComponent(String(docNumber).replace(/\//g, "---"));

/**
 * Abre el visor filtrado por una factura comercial (tipo 143)
 * @param {string} facidxxx Número de la factura
 */
export const openGenioVisorByFactura = (facidxxx) => {
  if (!facidxxx) {
    shootToast({
      variant: "warning",
      text: "No se encontró el soporte en genio documental.",
      duration: 5000,
      autoHide: true,
    });
    return;
  }
  openGenioVisor(
    `EMPNITXX=800251957:AREAIDXX=100:SERIEIDX=102:TIPOIDXX=143:REGESTXX=ACTIVO:JSONMETX->FACOMERX=${sanitizeDocNumber(facidxxx)}`
  );
};

/**
 * Abre el visor apuntando a un paquete documental puntual
 * @param {string|number} idarchxx   IDARCHXX del paquete en Genio
 * @param {string}        windowName Opcional: nombre de ventana propio
 */
export const openGenioVisorByIdArchivo = (idarchxx, windowName) =>
  openGenioVisor(`IDARCHXX=${idarchxx}`, windowName);

//Lado en el que se abre la próxima ventana del modo comparar (alterna)
let comparadorLado = 0;

/**
 * Abre el visor en modo COMPARAR (gestión documental): cada documento
 * abre SU PROPIA ventana (nombrada por IDARCHXX, así reabrir el mismo
 * documento reutiliza su ventana) y las ventanas se acomodan alternando
 * mitad derecha / mitad izquierda de la pantalla, para poder ver dos
 * documentos lado a lado al decidir un match o una vinculación.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-06
 * @param {string|number} idarchxx IDARCHXX del paquete en Genio
 */
export const openGenioVisorComparar = (idarchxx) => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!baseUrl) {
    return;
  }
  const width = Math.floor(window.screen.availWidth * 0.48);
  const height = window.screen.availHeight - 80;
  const left = comparadorLado === 0 ? window.screen.availWidth - width - 8 : 8;
  comparadorLado = comparadorLado === 0 ? 1 : 0;
  const features = `popup=yes,width=${width},height=${height},left=${left},top=40,resizable=yes,scrollbars=yes`;
  const genioWindow = window.open(`${baseUrl}/IDARCHXX=${idarchxx}`, `genioVisor-${idarchxx}`, features);
  if (genioWindow) {
    genioWindow.focus();
  }
};

/**
 * Abre el visor filtrado por un documento de transporte. El tipo
 * documental y el campo de metadata dependen del medio de transporte
 * (1 marítimo BL, 3 terrestre CPIC, 4 aéreo AWB).
 * @param {object} dataEdit Fila del DT (DTRCDOTR, DTRCMETR)
 */
export const openGenioVisorByDoctra = (dataEdit) => {
  const docNumber = dataEdit?.DTRCDOTR || "";
  if (docNumber === "") {
    shootToast({
      variant: "warning",
      text: "No se encontró el soporte en genio documental.",
      duration: 5000,
      autoHide: true,
    });
    return;
  }
  const genioByMedio = {
    1: { TIPOIDXX: 562, field: "NUMBLXXX" },
    3: { TIPOIDXX: 101, field: "DOCTRANS" },
    4: { TIPOIDXX: 147, field: "DOCTRANS" },
  };
  const config = genioByMedio[String(parseInt(dataEdit?.DTRCMETR, 10))] || genioByMedio[4];
  openGenioVisor(
    `EMPNITXX=800251957:AREAIDXX=100:SERIEIDX=100:TIPOIDXX=${config.TIPOIDXX}` +
      `:REGESTXX=ACTIVO:JSONMETX->${config.field}=${sanitizeDocNumber(docNumber)}`
  );
};
